import { formatDistanceToNow } from "date-fns";

// =============================================================================
// Types
// =============================================================================

export type DateFormatType =
  | "humanize"
  | "humanizeWAgo"
  | "ms"
  | "sec"
  | "min"
  | "date"
  | "dateFullMonth"
  | "log";

// =============================================================================
// Format String Constants (for backward compatibility)
// =============================================================================

/**
 * Format string constants matching the old moment format patterns
 * These are kept for backward compatibility with existing code
 */
export const formatStrings = {
  humanize: "humanize" as DateFormatType,
  humanizeWAgo: "humanizeWAgo" as DateFormatType,
  ms: "ms" as DateFormatType,
  sec: "sec" as DateFormatType,
  min: "min" as DateFormatType,
  date: "date" as DateFormatType,
  dateFullMonth: "dateFullMonth" as DateFormatType,
  log: "log" as DateFormatType,
} as const;

// =============================================================================
// Intl.DateTimeFormat Helpers
// =============================================================================

/**
 * Get a cached Intl.DateTimeFormat instance
 * Caching improves performance for repeated formatting
 */
const formatCache = new Map<string, Intl.DateTimeFormat>();

function getFormatter(timezone: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${timezone}-${JSON.stringify(options)}`;
  let formatter = formatCache.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-GB", { ...options, timeZone: timezone });
    formatCache.set(key, formatter);
  }
  return formatter;
}

/**
 * Pad a number with leading zeros
 */
function pad(num: number, size: number = 2): string {
  return String(num).padStart(size, "0");
}

/**
 * Get date parts in a specific timezone
 */
function getDatePartsInTimezone(
  date: Date,
  timezone: string
): {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  millisecond: number;
} {
  // Use Intl.DateTimeFormat to get parts in the target timezone
  const formatter = new Intl.DateTimeFormat("en-GB", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    fractionalSecondDigits: 3,
    hour12: false,
  });

  const parts = formatter.formatToParts(date);
  const partsMap: Record<string, string> = {};
  for (const part of parts) {
    partsMap[part.type] = part.value;
  }

  return {
    year: parseInt(partsMap.year || "0", 10),
    month: parseInt(partsMap.month || "1", 10),
    day: parseInt(partsMap.day || "1", 10),
    hour: parseInt(partsMap.hour || "0", 10),
    minute: parseInt(partsMap.minute || "0", 10),
    second: parseInt(partsMap.second || "0", 10),
    millisecond: parseInt(partsMap.fractionalSecond || "0", 10),
  };
}

/**
 * Get short month name (Jan, Feb, etc.)
 */
function getShortMonth(month: number): string {
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return months[month - 1] || "Jan";
}

/**
 * Get full month name (January, February, etc.)
 */
function getFullMonth(month: number): string {
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  return months[month - 1] || "January";
}

// =============================================================================
// Format Functions
// =============================================================================

/**
 * Format date as "YYYY-MM-DD HH:mm:ss.SSS" (log format)
 */
function formatLog(date: Date, timezone: string): string {
  const p = getDatePartsInTimezone(date, timezone);
  return `${p.year}-${pad(p.month)}-${pad(p.day)} ${pad(p.hour)}:${pad(p.minute)}:${pad(p.second)}.${pad(p.millisecond, 3)}`;
}

/**
 * Format date as "D MMM YYYY HH:mm:ss.SSS" (ms format)
 */
function formatMs(date: Date, timezone: string): string {
  const p = getDatePartsInTimezone(date, timezone);
  return `${p.day} ${getShortMonth(p.month)} ${p.year} ${pad(p.hour)}:${pad(p.minute)}:${pad(p.second)}.${pad(p.millisecond, 3)}`;
}

/**
 * Format date as "D MMM YYYY HH:mm:ss" (sec format)
 */
function formatSec(date: Date, timezone: string): string {
  const p = getDatePartsInTimezone(date, timezone);
  return `${p.day} ${getShortMonth(p.month)} ${p.year} ${pad(p.hour)}:${pad(p.minute)}:${pad(p.second)}`;
}

/**
 * Format date as "D MMM YYYY HH:mm" (min format)
 */
function formatMin(date: Date, timezone: string): string {
  const p = getDatePartsInTimezone(date, timezone);
  return `${p.day} ${getShortMonth(p.month)} ${p.year} ${pad(p.hour)}:${pad(p.minute)}`;
}

/**
 * Format date as "D MMM YYYY" (date format)
 */
function formatDateOnly(date: Date, timezone: string): string {
  const p = getDatePartsInTimezone(date, timezone);
  return `${p.day} ${getShortMonth(p.month)} ${p.year}`;
}

/**
 * Format date as "D MMMM YYYY" (dateFullMonth format)
 */
function formatDateFullMonth(date: Date, timezone: string): string {
  const p = getDatePartsInTimezone(date, timezone);
  return `${p.day} ${getFullMonth(p.month)} ${p.year}`;
}

// =============================================================================
// Humanize Functions
// =============================================================================

/**
 * Format a duration in milliseconds to human-readable text
 * Matches moment's humanize behavior
 */
export function humanize(timestamp: number, withSuffix: boolean = false): string {
  const now = Date.now();
  const diffMs = now - timestamp;
  const isPast = diffMs >= 0;
  const absDiffMs = Math.abs(diffMs);

  // Convert to various units
  const seconds = Math.floor(absDiffMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);

  let result: string;

  if (seconds < 45) {
    result = "a few seconds";
  } else if (seconds < 90) {
    result = "a minute";
  } else if (minutes < 45) {
    result = `${minutes} minutes`;
  } else if (minutes < 90) {
    result = "an hour";
  } else if (hours < 24) {
    result = `${hours} hours`;
  } else if (hours < 42) {
    result = "a day";
  } else if (days < 30) {
    result = `${days} days`;
  } else if (days < 45) {
    result = "a month";
  } else if (days < 365) {
    result = `${months} months`;
  } else if (days < 545) {
    result = "a year";
  } else {
    result = `${years} years`;
  }

  if (withSuffix) {
    result = isPast ? `${result} ago` : `in ${result}`;
  }

  return result;
}

// =============================================================================
// Main Format Function
// =============================================================================

/**
 * Format a date for display
 *
 * @param isoString - ISO date string or Date object
 * @param formatType - The format type to use
 * @param options - Optional settings
 * @returns Formatted date string
 */
export function formatDate(
  isoString: string | Date,
  formatType: DateFormatType = "sec",
  options?: { timezone?: string; utc?: boolean }
): string {
  const date = typeof isoString === "string" ? new Date(isoString) : isoString;
  if (isNaN(date.getTime())) return "";

  const timezone = options?.utc ? "UTC" : options?.timezone ?? "UTC";

  switch (formatType) {
    case "humanize":
      return humanize(date.getTime(), false);
    case "humanizeWAgo":
      return humanize(date.getTime(), true);
    case "log":
      return formatLog(date, timezone);
    case "ms":
      return formatMs(date, timezone);
    case "sec":
      return formatSec(date, timezone);
    case "min":
      return formatMin(date, timezone);
    case "date":
      return formatDateOnly(date, timezone);
    case "dateFullMonth":
      return formatDateFullMonth(date, timezone);
    default:
      return formatSec(date, timezone);
  }
}

/**
 * Check if a format type is a humanize format
 */
export function isHumanizeFormat(format: string): boolean {
  return format === "humanize" || format === "humanizeWAgo" || format.includes("humanize");
}
